/**
 * @fileoverview Toast Container (React port of the Angular toast display).
 * @module components/common/toast/ToastContainer
 * @description
 * Renders `useToast().activeToasts` from the toast store. Reproduces the toast
 * UI from the Angular `app.html` toast block and extends it to cover the full
 * `ToastMessage` surface that the ported store already supports: per-type
 * styling/icons (success/error/info/warning/retrying/offline/
 * connection-restored), optional action buttons, a dismiss button when
 * `dismissible`, and a progress bar when `progress` is defined.
 *
 * Auto-dismiss is owned by the store (setTimeout in `showToast`); this component
 * only renders and wires the dismiss/action click handlers.
 *
 * Accessibility: error/warning toasts use `role="alert"` + `aria-live="assertive"`;
 * all others use `role="status"` + `aria-live="polite"`.
 *
 * @author Liraz Amir
 */

import type { CSSProperties } from 'react';

import { useToast } from '@/hooks/useToast';
import type { ToastMessage } from '@/hooks/useToast';
import { SvgIcon } from '@/components/common/svg-icon/SvgIcon';

import './ToastContainer.scss';

/** Icon name per toast type (from the icon service / lucide set). */
const TYPE_ICON: Record<ToastMessage['type'], string> = {
  success: 'check-circle',
  error: 'alert-circle',
  warning: 'alert-triangle',
  info: 'info',
  retrying: 'refresh',
  offline: 'wifi-off-01',
  'connection-restored': 'wifi-connected-01',
};

/** Toasts that should announce assertively. */
const ASSERTIVE_TYPES: ReadonlySet<ToastMessage['type']> = new Set(['error', 'warning']);

/** Renders the stack of active toast notifications. */
export function ToastContainer() {
  const { activeToasts, removeToast } = useToast();

  if (activeToasts.length === 0) {
    return null;
  }

  return (
    <div className="toast-container" aria-live="polite">
      {activeToasts.map((toast) => {
        const assertive = ASSERTIVE_TYPES.has(toast.type);
        return (
          <div
            key={toast.id}
            className={`toast toast-${toast.type}`}
            role={assertive ? 'alert' : 'status'}
            aria-live={assertive ? 'assertive' : 'polite'}
          >
            <SvgIcon className="toast-icon" name={TYPE_ICON[toast.type]} size="small" />

            <div className="toast-body">
              <span className="toast-message">{toast.message}</span>

              {toast.actions && toast.actions.length > 0 && (
                <div className="toast-actions">
                  {toast.actions.map((action, index) => (
                    <button
                      key={`${toast.id}-action-${index}`}
                      type="button"
                      className={`toast-action toast-action-${action.variant ?? 'secondary'}`}
                      onClick={() => action.handler()}
                    >
                      {action.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {toast.dismissible && (
              <button
                type="button"
                className="toast-dismiss"
                aria-label="Dismiss notification"
                onClick={() => removeToast(toast.id)}
              >
                <SvgIcon name="x" size="small" />
              </button>
            )}

            {toast.progress !== undefined && (
              <div className="toast-progress-track">
                <div
                  className="toast-progress-bar"
                  role="progressbar"
                  aria-valuenow={toast.progress}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  style={{ '--toast-progress': `${toast.progress}%` } as CSSProperties}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default ToastContainer;
